(() => {
  const page = document.querySelector('[data-section-type="product-page"]');
  const productId = page?.dataset.productId || '';
  const handle = page?.dataset.productHandle || '';

  const viewportType = () => (window.innerWidth <= 767 ? 'mobile' : 'desktop');

  const getVariantId = () => {
    const input = document.querySelector('.m-product-form--main input[name="id"]:not([disabled])')
      || document.querySelector('.m-product-form--main input[name="id"]');
    return input?.value || '';
  };

  const getDeviceModel = () => {
    const select = document.querySelector('.m-product-form--main select[name^="options["]');
    return select?.value || '';
  };

  const fontLabels = {
    'classic-serif': 'Classic Serif',
    'signature-script': 'Signature Script',
    'elegant-script': 'Elegant Script',
    'romantic-script': 'Romantic Script',
    'natural-handwriting': 'Natural Handwriting',
    'modern-serif': 'Modern Serif',
    monogram: 'Monogram',
    'modern-sans': 'Modern Sans',
    'soft-rounded': 'Soft Rounded',
  };

  const analyticsEventPrefix = 'ucolor:';
  const analyticsFields = new Set([
    'product_id',
    'product_handle',
    'variant_id',
    'device_model',
    'font_key',
    'personalization_type',
    'price',
    'mobile_desktop',
    'has_text',
    'page_path',
    'selector_variant',
    'full_or_optional',
  ]);

  const publish = (name, values = {}) => {
    const eventName = name.startsWith(analyticsEventPrefix) ? name : `${analyticsEventPrefix}${name}`;
    const rawPayload = {
      product_id: productId,
      product_handle: handle,
      variant_id: getVariantId(),
      device_model: getDeviceModel(),
      mobile_desktop: viewportType(),
      ...values,
    };

    // Only send the approved analytics schema; freeform text, files and private properties never enter it.
    const payload = Object.fromEntries(
      Object.entries(rawPayload).filter(([key, value]) => analyticsFields.has(key) && value !== undefined && value !== null),
    );
    if (payload.has_text == null) payload.has_text = false;

    try {
      if (typeof window.Shopify?.analytics?.publish !== 'function') return undefined;
      const result = window.Shopify.analytics.publish(eventName, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
      return result;
    } catch (error) {
      // Analytics must never block product interaction.
      return undefined;
    }
  };

  const markAdvancedFields = (root, { keepPrimaryControls = false } = {}) => {
    if (!root || root.dataset.p1AdvancedPrepared === 'true') return;
    root.dataset.p1AdvancedPrepared = 'true';

    const selectors = [
      ...(keepPrimaryControls ? [] : ['[data-text-color]', '[data-text-size]']),
      '[data-text-center]',
      '[data-text-position]',
      '[data-text-rotate]',
      '[data-text-scale]',
      '[data-image-scale]',
      '[data-image-rotation]',
      '[data-frame-toggle]',
      '[data-frame-options]',
    ];

    root.querySelectorAll(selectors.join(',')).forEach((control) => {
      const shell = control.closest('label, .product-text-personalizer__field, .product-text-personalizer__frame-controls, .product-text-personalizer__frame-options, [class*="field"], [class*="control"]') || control.parentElement;
      shell?.classList.add('p1-full-advanced-control');
      if (control.id) {
        root.querySelector(`label[for="${CSS.escape(control.id)}"]`)?.classList.add('p1-full-advanced-control');
      }
    });

    root.querySelectorAll('[class*="tip"], [data-text-tip], [data-image-tip]').forEach((tip) => {
      tip.classList.add('p1-full-advanced-control');
    });

    root.querySelectorAll(
      '[class*="phone-case-customizer__field"], [class*="phone-case-customizer__field-grid"], .phone-case-customizer__panel-tip, .product-text-personalizer__text-handle'
    ).forEach((control) => {
      if (control.querySelector('[data-text-input], input[type="file"]')) return;
      control.classList.add('p1-full-advanced-control');
    });

    const markDynamicHandles = () => {
      root.querySelectorAll('.product-text-personalizer__text-handle').forEach((control) => {
        control.classList.add('p1-full-advanced-control');
      });
    };
    markDynamicHandles();
    if (root.dataset.p1HandleObserverBound !== 'true') {
      root.dataset.p1HandleObserverBound = 'true';
      const handleObserver = new MutationObserver(markDynamicHandles);
      handleObserver.observe(root, { childList: true, subtree: true });
    }
  };

  const replaceTechnicalCopy = (root) => {
    if (!root) return;
    const legacyCopy = /leave\s+this\s+turned\s+off|we\s+will\s+attach\s+the\s+preview\s+to\s+cart\s+properties|your\s+preview\s+will\s+be\s+saved\s+with\s+your\s+order/gi;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const replacement = 'Your personalized preview will be saved with your order.';
    let node;

    while ((node = walker.nextNode())) {
      if (!legacyCopy.test(node.nodeValue || '')) continue;
      legacyCopy.lastIndex = 0;
      node.nodeValue = replacement;
    }

    if (root.dataset.p1CopyObserverBound === 'true') return;
    root.dataset.p1CopyObserverBound = 'true';
    const observer = new MutationObserver(() => replaceTechnicalCopy(root));
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  };

  const addAdvancedToggle = (root, label, onChange) => {
    if (!root || root.querySelector('.p1-full-advanced-toggle')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'p1-full-advanced-toggle';
    button.textContent = label;
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', () => {
      const open = root.dataset.p1AdvancedOpen !== 'true';
      root.dataset.p1AdvancedOpen = open ? 'true' : 'false';
      button.textContent = open ? 'Hide advanced adjustments' : label;
      button.setAttribute('aria-expanded', String(open));
      onChange?.(open);
    });

    root.prepend(button);
  };

  const limitAmberFonts = (app) => {
    const font = app?.querySelector('[data-text-font]');
    if (!font || font.dataset.p1FontLimitApplied === 'true') return;
    const allowed = new Set(['classic-serif', 'signature-script', 'elegant-script', 'modern-serif', 'monogram', 'modern-sans', 'soft-rounded', 'natural-handwriting']);
    Array.from(font.options).forEach((option) => {
      option.hidden = !allowed.has(option.value);
    });
    font.dataset.p1FontLimitApplied = 'true';
  };

  const initCartSummaries = () => {
    const parseJson = (raw) => {
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'string') {
          try { return JSON.parse(parsed); } catch (error) { return parsed; }
        }
        return parsed;
      } catch (error) {
        try { return JSON.parse(raw.replace(/\\"/g, '"')); } catch (nestedError) { return null; }
      }
    };

    document.querySelectorAll('[data-cart-personalization-summary]').forEach((summary) => {
      if (summary.dataset.p1SummaryBound === 'true') return;
      summary.dataset.p1SummaryBound = 'true';

      const data = parseJson(summary.dataset.p1TextJson || '');
      const customization = summary.dataset.p1Customization || '';
      const mode = summary.dataset.p1Mode || '';
      const text = data?.content || data?.text || data?.text?.content || '';
      const value = summary.querySelector('[data-p1-summary-value]');
      if (value) {
        if (text) value.textContent = text;
        else if (customization && !customization.includes('{')) value.textContent = customization;
        else if (mode === 'image') value.textContent = 'Photo uploaded';
        else value.textContent = 'Personalized';
      }

      const fontKey = data?.fontKey || data?.font_key || data?.text?.fontKey || '';
      const font = summary.querySelector('[data-p1-summary-font]');
      if (font && fontLabels[fontKey] && (text || mode === 'text' || mode === 'image-text' || mode === 'text_only_optional')) {
        font.textContent = `Font: ${fontLabels[fontKey]}`;
        font.hidden = false;
      }
    });

    if (!window.__p1CartSummaryObserver && document.body) {
      window.__p1CartSummaryObserver = new MutationObserver(() => initCartSummaries());
      window.__p1CartSummaryObserver.observe(document.body, { childList: true, subtree: true });
    }
  };

  const initOptionalPixelName = () => {
    const pixelPage = document.querySelector('[data-personalization-surface-family="google-pixel"]');
    if (pixelPage?.querySelector('[data-p1-personalization-chooser]')) return;
    const app = pixelPage?.querySelector('[data-product-text-personalizer]');
    const controls = app?.querySelector('[data-controls]');
    const template = pixelPage?.querySelector('[data-name-position-controls]');
    if (!app || !controls || !template || app.dataset.p1OptionalNameBound === 'true') return;
    app.dataset.p1OptionalNameBound = 'true';
    app.dataset.p1AdvancedOpen = 'false';
    limitAmberFonts(app);
    markAdvancedFields(app, { keepPrimaryControls: true });
    replaceTechnicalCopy(app);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'p1-personalization-advanced-toggle';
    button.textContent = template.dataset.advancedLabel;
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', () => {
      const open = app.dataset.p1AdvancedOpen !== 'true';
      app.dataset.p1AdvancedOpen = String(open);
      button.textContent = open ? template.dataset.hideAdvancedLabel : template.dataset.advancedLabel;
      button.setAttribute('aria-expanded', String(open));
    });
    controls.appendChild(button);
  };

  const initAmber = () => {
    const chooser = document.querySelector('[data-p1-personalization-chooser]');
    const scope = chooser?.closest('.m-main-product--wrapper') || document;
    const app = scope.querySelector('[data-product-text-personalizer]');
    const toggle = app?.querySelector('[data-enabled-toggle]') || document.querySelector('[data-enabled-toggle]');
    if (!chooser || !app || !toggle || chooser.dataset.p1Bound === 'true') return;
    chooser.dataset.p1Bound = 'true';
    app.dataset.p1Chooser = 'true';

    const selectorVariant = new URLSearchParams(window.location.search).get('p1_selector')?.toLowerCase() === 'b' ? 'b' : 'a';
    chooser.dataset.p1Selector = selectorVariant;

    const input = app.querySelector('[data-text-input]');
    const font = app.querySelector('[data-text-font]');
    const state = chooser.querySelector('[data-p1-state]');
    const samplePreviewMessage = chooser.dataset.samplePreviewMessage;
    const cards = Array.from(chooser.querySelectorAll('[data-p1-mode]'));
    const upgrade = chooser.querySelector('[data-p1-upgrade]');
    const advancedToggle = chooser.querySelector('[data-p1-advanced-toggle]');
    const customForms = Array.from(document.querySelectorAll('form[data-type="add-to-cart-form"]'));
    let previewReported = false;
    let textStartedReported = false;
    let lastAddToCartAt = 0;
    const pricePairs = JSON.parse(chooser.querySelector('[data-p1-price-pairs]')?.textContent || '[]');
    const currentPair = () => {
      const id = String(getVariantId());
      return pricePairs.find((pair) => String(pair.standardId) === id || String(pair.customId) === id);
    };
    const currentPrice = () => {
      const pair = currentPair();
      return pair ? (toggle.checked ? pair.customPrice : pair.standardPrice) / 100 : undefined;
    };
    const updatePrices = () => {
      const pair = currentPair();
      if (!pair) return;
      chooser.querySelector('[data-p1-mode="standard"] .p1-personalization-card__price').textContent = pair.standardLabel;
      chooser.querySelector('[data-p1-mode="custom"] .p1-personalization-card__price').textContent = `+${pair.upgradeLabel} · ${pair.customLabel}`;
      if (upgrade) upgrade.textContent = `✨ Add your name for only ${pair.upgradeLabel}`;
      const note = chooser.querySelector('[data-p1-selector-b]');
      if (note) note.textContent = `Standard · ${pair.standardLabel} · Original design without personalization`;
    };


    limitAmberFonts(app);
    markAdvancedFields(app, { keepPrimaryControls: true });
    replaceTechnicalCopy(app);
    app.dataset.p1AdvancedOpen = 'false';

    const updateState = () => {
      updatePrices();
      const custom = Boolean(toggle.checked);
      chooser.classList.toggle('is-custom', custom);
      chooser.dataset.mode = custom ? 'custom' : 'standard';
      app.hidden = !custom;
      cards.forEach((card) => {
        card.setAttribute('aria-pressed', String(card.dataset.p1Mode === (custom ? 'custom' : 'standard')));
      });
      if (state) {
        state.textContent = custom
          ? (input?.value.trim() ? 'Your personalized preview is ready.' : samplePreviewMessage)
          : 'Original design without personalization.';
      }
      if (upgrade) upgrade.hidden = custom;
      if (advancedToggle) advancedToggle.hidden = !custom;
      if (custom && input) input.placeholder = 'e.g. EMILY';
    };

    const choose = (mode, focusInput = false) => {
      const custom = mode === 'custom';
      const changed = Boolean(toggle.checked) !== custom;
      if (changed) toggle.click();
      window.setTimeout(updateState, 80);
      if (!changed) {
        if (custom && focusInput && input) window.setTimeout(() => input.focus({ preventScroll: true }), 120);
        return;
      }
      publish('ucolor:personalization_selected', {
        personalization_type: custom ? 'custom_text' : 'standard',
        price: currentPrice(),
        has_text: Boolean(input?.value.trim()),
      });
      if (custom && focusInput && input) {
        window.setTimeout(() => input.focus({ preventScroll: true }), 120);
      }
    };

    cards.forEach((card) => card.addEventListener('click', () => choose(card.dataset.p1Mode, card.dataset.p1Mode === 'custom')));
    upgrade?.addEventListener('click', () => choose('custom', true));
    toggle.addEventListener('change', updateState);

    advancedToggle?.addEventListener('click', () => {
      const open = app.dataset.p1AdvancedOpen !== 'true';
      app.dataset.p1AdvancedOpen = open ? 'true' : 'false';
      advancedToggle.textContent = open ? 'Hide advanced options' : 'Advanced options';
      advancedToggle.setAttribute('aria-expanded', String(open));
    });

    input?.addEventListener('input', () => {
      const hasText = Boolean(input.value.trim());
      if (state && toggle.checked) state.textContent = hasText ? 'Your personalized preview is ready.' : samplePreviewMessage;
      if (hasText && !textStartedReported) {
        textStartedReported = true;
        publish('ucolor:personalization_text_started', {
          personalization_type: 'custom_text',
          has_text: true,
          price: currentPrice(),
        });
      }
      if (hasText && !previewReported) {
        previewReported = true;
        publish('ucolor:personalization_preview_ready', {
          personalization_type: 'custom_text',
          has_text: true,
          price: currentPrice(),
        });
      }
      if (!hasText) {
        textStartedReported = false;
        previewReported = false;
      }
    });

    font?.addEventListener('change', () => {
      publish('ucolor:personalization_font_selected', {
        personalization_type: 'custom_text',
        font_key: font.value,
        has_text: Boolean(input?.value.trim()),
        price: currentPrice(),
      });
    });

    customForms.forEach((form) => {
      if (form.dataset.p1SubmitBound === 'true') return;
      form.dataset.p1SubmitBound = 'true';
      form.addEventListener('submit', (event) => {
        if (toggle.checked && !input?.value.trim()) {
          event.preventDefault();
          if (state) state.textContent = 'Add a name before adding your personalized product.';
          input?.focus({ preventScroll: true });
          return;
        }
        if (toggle.checked) {
          const now = Date.now();
          if (now - lastAddToCartAt < 500) return;
          lastAddToCartAt = now;
          publish('ucolor:personalized_add_to_cart', {
            personalization_type: 'custom_text',
            font_key: font?.value || '',
            price: currentPrice(),
            has_text: true,
          });
        }
      }, true);
    });

    document.querySelectorAll('.m-main-product--info select[name^="options["]').forEach((select) => {
      select.addEventListener('change', () => window.setTimeout(updateState, 120));
    });

    updateState();
    publish('ucolor:personalization_offer_viewed', {
      personalization_type: 'standard',
      price: currentPrice(),
      has_text: false,
    });
  };

  const initFull = () => {
    const buttons = Array.from(document.querySelectorAll('button[data-mode-option]'));
    if (!buttons.length || buttons[0].dataset.p1Bound === 'true') return;
    buttons.forEach((button) => { button.dataset.p1Bound = 'true'; });

    const customizer = document.querySelector('[data-product-customizer]')
      || document.querySelector('.phone-case-customizer__controls')
      || buttons[0].closest('.phone-case-customizer__controls')
      || buttons[0].closest('.product-customizer')
      || buttons[0].parentElement?.parentElement;
    if (!customizer) return;
    customizer.classList.add('p1-personalization-root');
    customizer.dataset.p1AdvancedOpen = 'false';
    markAdvancedFields(customizer);
    addAdvancedToggle(customizer, 'More advanced adjustments', (open) => {
      customizer.dataset.p1AdvancedOpen = open ? 'true' : 'false';
    });

    const labels = {
      image: 'Upload a Photo',
      text: 'Add Text',
      'image-text': 'Photo + Text',
    };
    let lastFullMode = buttons.find((button) => button.getAttribute('aria-pressed') === 'true' || button.dataset.selected === 'true')?.dataset.modeOption || '';
    buttons.forEach((button) => {
      const mode = button.dataset.modeOption;
      if (labels[mode]) button.textContent = labels[mode];
      button.addEventListener('click', () => {
        if (mode === lastFullMode) return;
        lastFullMode = mode;
        publish('ucolor:personalization_selected', {
          personalization_type: mode || 'custom',
          price: 29.95,
          has_text: Boolean(customizer.querySelector('[data-text-input]')?.value.trim()),
        });
      });
    });

    const input = customizer.querySelector('[data-text-input]');
    const font = customizer.querySelector('[data-text-font]');
    let textStartedReported = false;
    let previewReported = false;
    input?.addEventListener('input', () => {
      const hasText = Boolean(input.value.trim());
      if (hasText && !textStartedReported) {
        textStartedReported = true;
        publish('ucolor:personalization_text_started', {
          personalization_type: 'text',
          has_text: true,
          price: 29.95,
        });
      }
      if (hasText && !previewReported) {
        previewReported = true;
        publish('ucolor:personalization_preview_ready', {
          personalization_type: 'text',
          has_text: true,
          price: 29.95,
        });
      }
      if (!hasText) {
        textStartedReported = false;
        previewReported = false;
      }
    });
    font?.addEventListener('change', () => publish('ucolor:personalization_font_selected', {
      personalization_type: 'text',
      font_key: font.value,
      has_text: Boolean(input?.value.trim()),
      price: 29.95,
    }));

    document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
      if (form.dataset.p1FullSubmitBound === 'true') return;
      form.dataset.p1FullSubmitBound = 'true';
      form.addEventListener('submit', (event) => {
        const pressed = customizer.querySelector('button[data-mode-option][aria-pressed="true"]') || customizer.querySelector('button[data-mode-option][data-selected="true"]');
        const mode = pressed?.dataset.modeOption || 'custom';
        const hasText = Boolean(input?.value.trim());
        if ((mode === 'text' || mode === 'image-text') && !hasText) {
          event.preventDefault();
          input?.focus({ preventScroll: true });
          return;
        }
        const now = Date.now();
        const lastAddToCartAt = Number(form.dataset.p1LastAddToCartAt || 0);
        if (now - lastAddToCartAt < 500) return;
        form.dataset.p1LastAddToCartAt = String(now);
        publish('ucolor:personalized_add_to_cart', {
          personalization_type: mode,
          font_key: font?.value || '',
          price: 29.95,
          has_text: hasText,
        });
      }, true);
    });

    publish('ucolor:personalization_offer_viewed', {
      personalization_type: 'custom_product',
      price: 29.95,
      has_text: false,
    });
  };

  const parsePersonalizationPayload = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') {
        try { return JSON.parse(parsed); } catch (error) { return parsed; }
      }
      return parsed;
    } catch (error) {
      try { return JSON.parse(raw.replace(/\\"/g, '"')); } catch (nestedError) { return null; }
    }
  };

  const getPersonalizedCartItems = () => Array.from(
    document.querySelectorAll('[data-cart-personalization-summary]'),
  ).map((summary) => {
    const mode = (summary.dataset.p1Mode || '').trim().toLowerCase().replace(/_/g, '-');
    const customization = (summary.dataset.p1Customization || '').trim().toLowerCase();
    const data = parsePersonalizationPayload(summary.dataset.p1TextJson || '');
    const text = typeof data?.content === 'string'
      ? data.content.trim()
      : typeof data?.text === 'string'
        ? data.text.trim()
        : typeof data?.text?.content === 'string'
          ? data.text.content.trim()
          : '';
    const standardMode = !mode || ['standard', 'original', 'none'].includes(mode);
    const standardCustomization = !customization
      || /^(standard|original|original design without personalization)$/.test(customization);
    const hasImageMode = ['image', 'image-text', 'photo', 'photo-text'].includes(mode);
    const personalized = !standardMode || Boolean(text) || hasImageMode || !standardCustomization;
    return { personalized, hasText: Boolean(text) };
  }).filter((item) => item.personalized);

  const bindCheckoutAnalytics = () => {
    document.querySelectorAll('[name="checkout"], a[href*="/checkout"]').forEach((element) => {
      if (element.dataset.p1CheckoutBound === 'true') return;
      element.dataset.p1CheckoutBound = 'true';
      element.addEventListener('click', (event) => {
        const personalizedItems = getPersonalizedCartItems();
        if (!personalizedItems.length) return;
        if (element.dataset.p1CheckoutPending === 'true') {
          event.preventDefault();
          return;
        }
        element.dataset.p1CheckoutPending = 'true';
        event.preventDefault();
        const publishResult = publish('ucolor:personalized_checkout_started', {
          personalization_type: 'cart',
          has_text: personalizedItems.some((item) => item.hasText),
        });
        const continueToCheckout = () => {
          if (element.form) {
            const checkoutField = document.createElement('input');
            checkoutField.type = 'hidden';
            checkoutField.name = 'checkout';
            checkoutField.value = 'CHECK OUT';
            element.form.appendChild(checkoutField);
            HTMLFormElement.prototype.submit.call(element.form);
          } else if (element.href) {
            window.location.href = element.href;
          }
        };
        const sent = publishResult && typeof publishResult.then === 'function'
          ? publishResult.catch(() => {})
          : Promise.resolve();
        Promise.race([
          sent,
          new Promise((resolve) => window.setTimeout(resolve, 500)),
        ]).then(continueToCheckout);
      });
    });
  };

  const init = () => {
    initCartSummaries();
    initAmber();
    initOptionalPixelName();
    initFull();
    bindCheckoutAnalytics();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.addEventListener('load', init, { once: true });
  window.setTimeout(init, 1200);
})();

